// @flow
import React, { type Node } from 'react';
import { compose, pure } from 'recompose';
import { get } from 'lodash';
import { Col, Row, Button } from 'angieslist-react-components';
import { legacyAnalytics, analyticsActions, LegacyTrack, urlCatalog } from 'angieslist-app-platform';
import { getCookie, setCookie } from 'angieslist-app-platform/lib/common/utils/cookieUtils';
import { defaultProps } from '../../../../../app/browser/analyticsDefaultProperties';
import SendEnhancedTrackEventOnce from '../../../../../app/browser/SendEnhancedTrackEventOnce';
import { itemClicked } from '../../../../../app/browser/analyticsHelpers';
import ErrorModal from '../../../../../app/browser/ErrorModal';
import agreementAddOnActions from '../../../../../app/common/agreementAddOn/agreementAddOnActions';


import './ManageLikeAProCard.scss';

const productImage = require('../../../static/business-management-tools-subscription/collect-a-payment.png');
const quoteImage = require('../../../static/business-management-tools-subscription/quotes24.svg');
const graphImage = require('../../../static/business-management-tools-subscription/graph24.svg');

const legalTCsLink = urlCatalog.global.advertisingAndDealsAgreement();

type Props = {
  activityLocation: string,
  cookieKey: string,
  serviceProviderId: string,
  createAndActivateBusinessManagementToolsSubscription:
    typeof agreementAddOnActions.createAndActivateBusinessManagementToolsSubscription,
  sendEnhancedTrackEvent:
    typeof analyticsActions.sendEnhancedTrackEvent,
};

type State = {
    hideCard: boolean,
    activationIsLoading: bool,
    showErrorModal: bool,
    errorInfo?: {
      headlineText: string,
      detailsText: string,
    },
  };

const getCookieKey = key => `hideManageLikeAProCard-${key}`;

export class ManageLikeAProCardComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hideCard: process.env.IS_BROWSER ? (getCookie(getCookieKey(props.cookieKey)) === 'true') : false,
      activationIsLoading: false,
      showErrorModal: false,
      errorInfo: undefined,
    };
  }

  state: State;
  props: Props;

  makeAddFeaturesErrorInfo: ((
  context: string,
  status: number
) => {|
  detailsText: string,
  headlineText: string,
  segmentData: {|name: string, properties: any|},
|}) = (context: string, status: number) => {
  const { activityLocation } = this.props;

  const segmentData = {
    name: 'Modal Fired',
    properties: {
      activityLocation,
      description: `${activityLocation} - Error("${context}", status=${status})`,
      ...defaultProps,
    },
  };

  if (status === 403) {
    return {
      headlineText: 'Invalid Action',
      detailsText: `You must be an administrator to add this feature subscription.
                      If you continue to have trouble, please contact your representative for assistance.`,
      segmentData,
    };
  }
  return {
    headlineText: 'Something went wrong.',
    detailsText: `Please go back and try again. If you continue to get this error, contact your
                    representative for assistance.`,
    segmentData,
  };
};

  handleAddFeaturesClick: (() => Promise<void>) = async () => {
    const { serviceProviderId, createAndActivateBusinessManagementToolsSubscription } = this.props;

    try {
      this.setState({ activationIsLoading: true });
      await createAndActivateBusinessManagementToolsSubscription(Number(serviceProviderId));
      const thankYouURL =
        urlCatalog.office.subscriptions.businessManagement.signUpThankYou(serviceProviderId);
      window.location.assign(thankYouURL);
    } catch (err) {
      this.setState({
        activationIsLoading: false,
        showErrorModal: true,
        errorInfo: this.makeAddFeaturesErrorInfo('Add Features click', err.status),
      });
    }
  }

  closeErrorModal: (() => void) = () => {
    this.setState({ showErrorModal: false, errorInfo: undefined });
  }

  render(): Node {
    const learnMoreLink =
      urlCatalog.office.subscriptions.businessManagement.signUp(this.props.serviceProviderId);

    return (
      <If condition={!this.state.hideCard}>
        <React.Fragment>
          <SendEnhancedTrackEventOnce
            sendEnhancedTrackEvent={this.props.sendEnhancedTrackEvent}
            analyticsEventName="Item Displayed"
            analyticsEventProperties={{
              activityLocation: this.props.activityLocation,
              description: 'Manage Like a Pro Card',
              ...defaultProps,
            }}
          />
          <div className="manage-like-a-pro-card">
            <div className="card-container desktop-background">
              <Row className="card-header-padding">
                <Col xs={12} sm={8} smOffset={4}>
                  <div className="card-header" >
                    <div className="card-header-copy h4">
                      Manage Your Business Like a Pro
                    </div>
                    <LegacyTrack>
                      <Button
                        id="manage-like-a-pro-card--close-card-button"
                        className="close-button"
                        bsStyle="link"
                        onClick={() => {
                          const expireDate = new Date();
                          expireDate.setTime(expireDate.getTime() + (1 * 24 * 60 * 60 * 1000));
                          setCookie(
                            getCookieKey(this.props.cookieKey),
                            true,
                            expireDate.toUTCString(),
                          );
                          this.setState({ hideCard: true });
                        }}
                      >
                        <i className="icon icon-close close-icon" />
                      </Button>
                    </LegacyTrack>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col className="card-body-padding card-body">
                  <Row>
                    <Col xs={12} sm={4} className="product-image-wrapper">
                      <img className="product-image" alt="" src={productImage} />
                    </Col>
                    <Col xs={12} sm={8} className="product-pitch-wrapper">
                      <Row>
                        <Col xs={12}>
                          <span className="product-pitch--add-some-tools-copy desktop-only-inline">
                            Add some tools to your business toolbox!
                            As an Angi advertiser,
                            you are eligible to try out some great new features.  You can use them
                            right in the Pro App.
                          </span>
                          <span className="product-pitch--add-some-tools-copy mobile-only-inline">
                            Add some tools to your business toolbox!  You can use these new features
                            right in the Pro App.
                          </span>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} className="product-pitch--line-items-wrapper">
                          <div className="product-pitch--line-item">
                            <img className="product-pitch--line-item-image" alt="" src={quoteImage} />
                            <div className="product-pitch--line-item-copy">
                              Send Job Quotes, Invoices, and Take Payments Directly
                            </div>
                          </div>
                          <div className="product-pitch--line-item">
                            <img className="product-pitch--line-item-image" alt="" src={graphImage} />
                            <div className="product-pitch--line-item-copy">
                            Integrate with Your QuickBooks Online Account*
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} className="product-pitch--pricing-wrapper">
                          <span className="product-pitch--pricing-copy">
                                New features are $0.50/month, billed at the same cadence
                                as your advertising.
                            <span className="desktop-only-inline"> By clicking Add Features, you agree to the</span>
                            <span className="mobile-only-inline"> For more information, see</span>
                            <a
                              href={legalTCsLink}
                              id="manage-like-a-pro-card--terms-and-conditions-link"
                              target="blank"
                            >
                              {' '}
                              Terms and Conditions
                            </a>
                            .
                          </span>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} className="mobile-background">
                          <Row>
                            <Col xs={12} sm={12} className="product-pitch--buttons-wrapper">
                              <LegacyTrack>
                                <Button
                                  href={learnMoreLink}
                                  id="manage-like-a-pro-card--learn-more-button"
                                  bsSize="small"
                                  flat
                                  className="product-pitch--learn-more-button"
                                >
                                  Learn More
                                </Button>
                              </LegacyTrack>
                              <LegacyTrack>
                                <Button
                                  id="manage-like-a-pro-card--add-features-button"
                                  bsSize="small"
                                  bsStyle="primary-office"
                                  flat
                                  className="product-pitch--add-features-button"
                                  onClick={this.handleAddFeaturesClick}
                                  loading={this.state.activationIsLoading}
                                  disabled={this.state.activationIsLoading}
                                >
                                  Add Features
                                </Button>
                              </LegacyTrack>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={12} className="product-pitch--footnotes-wrapper">
                              <p>*Must have a QuickBooks Online account to be able to sync
                                    Angi data to QuickBooks.
                              </p>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </div>
          <ErrorModal
            show={this.state.showErrorModal}
            onClose={this.closeErrorModal}
            sendEnhancedTrackEvent={this.props.sendEnhancedTrackEvent}
            headlineText={get(this.state, 'errorInfo.headlineText')}
            detailsText={get(this.state, 'errorInfo.detailsText')}
            enhanceableSegmentData={get(this.state, 'errorInfo.segmentData')}
          />
        </React.Fragment>
      </If>
    );
  }
}

const analyticsRuleset = {
  elements: {
    'manage-like-a-pro-card--close-card-button': itemClicked('SP : Home', 'Manage Like a Pro Card - Close Card'),
    'manage-like-a-pro-card--learn-more-button': itemClicked('SP : Home', 'Manage Like a Pro Card - Learn More'),
    'manage-like-a-pro-card--add-features-button': itemClicked('SP : Home', 'Manage Like a Pro Card -- Add Features'),
  },
};

export default (compose(
  legacyAnalytics({
    ruleset: analyticsRuleset,
  }),
  pure,
)(ManageLikeAProCardComponent): any);



// WEBPACK FOOTER //
// ./src/modules/home/browser/components/DashboardV2/ManageLikeAProCard.js